import { type MatterSettings } from "@/lib/data/requests/matters/matter-settings";
import {
  createMatterTemplate,
  deleteMatterTemplate,
  generateMatterTemplateDocument,
  type GenerateMatterTemplateDocumentInput,
  retrieveMatterTemplates,
  updateMatterTemplate,
} from "@/lib/data/requests/matters/matter-template";
import { generateS3ItemPreSignedUrlV1 } from "@/lib/data/requests/s3";
import { queryKeys } from "@/lib/data/static";
import type { MatterTemplate } from "@/lib/types";
import { type APIError, type APIResponse } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { getFileNameParts } from "@/lib/utils/matterFile";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { orderBy } from "lodash";
import toast from "react-hot-toast";

export const retrieveMatterTemplatesQueryOptions = () => {
  return {
    queryKey: [queryKeys.matterSettings, "templates"],
    queryFn: () => retrieveMatterTemplates(),
    fetchOnWindowFocus: false,
  };
};

const upsertMatterTemplateInMatterSettingsCache = (
  oldData: APIResponse<MatterSettings> | undefined,
  template: MatterTemplate,
) => {
  if (!oldData) return oldData;

  const existingTemplates = oldData.data.templates;
  const nextTemplates = existingTemplates.some(
    (existingTemplate) => existingTemplate.id === template.id,
  )
    ? existingTemplates.map((existingTemplate) =>
        existingTemplate.id === template.id ? template : existingTemplate,
      )
    : [...existingTemplates, template];

  return {
    ...oldData,
    data: {
      ...oldData.data,
      templates: orderBy(
        nextTemplates,
        ["matterTemplateLabel", "name"],
        ["asc", "asc"],
      ),
    },
  };
};

const upsertMatterTemplateInTemplatesCache = (
  oldData: APIResponse<MatterTemplate[]> | undefined,
  template: MatterTemplate,
) => {
  if (!oldData) return oldData;

  const existingTemplates = oldData.data;
  const nextTemplates = existingTemplates.some(
    (existingTemplate) => existingTemplate.id === template.id,
  )
    ? existingTemplates.map((existingTemplate) =>
        existingTemplate.id === template.id ? template : existingTemplate,
      )
    : [...existingTemplates, template];

  return {
    ...oldData,
    data: orderBy(
      nextTemplates,
      ["matterTemplateLabel", "name"],
      ["asc", "asc"],
    ),
  };
};

const syncMatterTemplateCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  template: MatterTemplate,
) => {
  queryClient.setQueryData(
    [queryKeys.matterSettings],
    (oldData: APIResponse<MatterSettings> | undefined) =>
      upsertMatterTemplateInMatterSettingsCache(oldData, template),
  );
  queryClient.setQueryData(
    [queryKeys.matterSettings, "templates"],
    (oldData: APIResponse<MatterTemplate[]> | undefined) =>
      upsertMatterTemplateInTemplatesCache(oldData, template),
  );
};

export const useCreateMatterTemplate = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: Partial<MatterTemplate> & {
        file?: File | undefined;
        operationId?: string;
        onSourceUploaded?: (key: string) => void;
      },
    ) => {
      let presignedUrlKey = data.presignedUrlKey;
      let templateType = data.type;
      if (data.file && !presignedUrlKey) {
        const presignedURLRes = await generateS3ItemPreSignedUrlV1(
          "matter-template",
        )({
          clientMethod: "put_object",
          fileName: data.file.name,
        });

        const uploadResponse = await fetch(presignedURLRes.data.presignedUrl, {
          method: "PUT",
          headers: {
            "Content-Type": data.file.type,
          },
          body: data.file,
        });
        if (!uploadResponse.ok) throw new Error("Template upload failed");
        presignedUrlKey = presignedURLRes.data.key;
        data.onSourceUploaded?.(presignedUrlKey);
        templateType =
          templateType || getFileNameParts(data.file.name).fileExtension;
      }

      return await createMatterTemplate({
        ...(data.operationId ? { operationId: data.operationId } : {}),
        category: data.category ?? [],
        description: data.description ?? null,
        moduleShortcut: data.moduleShortcut,
        name: data.name ?? data.file?.name ?? "",
        presignedUrlKey,
        type:
          templateType ||
          getFileNameParts(data.name ?? presignedUrlKey ?? undefined)
            .fileExtension,
        variables: data.variables,
      });
    },
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              templates: orderBy(
                [...oldData.data.templates, { ...data, id: "temp" }],
                ["matterTemplateLabel"],
                ["asc"],
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      if (showToast) {
        toastAPIErrorMessage(error, "Error creating matter template");
      }
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [queryKeys.matterSettings] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.files] }),
      ]);
    },
    onSuccess: (response) => {
      syncMatterTemplateCaches(queryClient, response.data);
      if (showToast) {
        toast.success("Matter template created successfully");
      }
    },
  });
};

export const useDeleteMatterTemplate = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterTemplate>[0]) =>
      deleteMatterTemplate(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              templates: oldData.data.templates.filter(
                (matterTemplate) => matterTemplate.id !== data.id,
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      if (showToast) {
        toastAPIErrorMessage(error, "Error deleting matter template");
      }
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [queryKeys.matterSettings] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.files] }),
      ]);
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Matter template deleted successfully");
      }
    },
  });
};

export const useBulkDeleteMatterTemplates = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(
        ids.map((id) => deleteMatterTemplate({ id })),
      );
      return {
        deletedIds: ids.filter(
          (_, index) => results[index].status === "fulfilled",
        ),
        failedIds: ids.filter(
          (_, index) => results[index].status === "rejected",
        ),
      };
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [queryKeys.matterSettings] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.files] }),
      ]);
    },
  });
};

export const useUpdateMatterTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterTemplate>[0]) =>
      updateMatterTemplate(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              templates: oldData.data.templates.map((matterTemplate) =>
                matterTemplate.id === data.id
                  ? { ...matterTemplate, ...data }
                  : matterTemplate,
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating matter template");
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [queryKeys.matterSettings] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.files] }),
      ]);
    },
    onSuccess: (response) => {
      syncMatterTemplateCaches(queryClient, response.data);
      toast.success("Matter template updated successfully");
    },
  });
};

export const useDuplicateMatterTemplate = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      sourceTemplate,
      name,
    }: {
      sourceTemplate: MatterTemplate;
      name: string;
    }) => {
      if (!sourceTemplate.presignedUrlKey) {
        throw new Error("Template file is not available to duplicate.");
      }

      const presignedUrlResponse = await generateS3ItemPreSignedUrlV1(
        "matter-template",
      )({
        clientMethod: "get_object",
        key: sourceTemplate.presignedUrlKey,
      });

      const response = await fetch(presignedUrlResponse.data.presignedUrl);
      if (!response.ok) {
        throw new Error(
          `Failed to download source template: ${response.status}`,
        );
      }

      const blob = await response.blob();
      const contentType =
        response.headers.get("content-type") ||
        blob.type ||
        "application/octet-stream";
      const file = new File([blob], name, { type: contentType });

      const uploadPresignedRes = await generateS3ItemPreSignedUrlV1(
        "matter-template",
      )({
        clientMethod: "put_object",
        fileName: file.name,
      });

      await fetch(uploadPresignedRes.data.presignedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
      });

      return createMatterTemplate({
        name,
        description: sourceTemplate.description ?? "",
        category: sourceTemplate.category ?? [],
        presignedUrlKey: uploadPresignedRes.data.key,
        type: sourceTemplate.type || getFileNameParts(name).fileExtension,
        variables: sourceTemplate.variables,
      });
    },
    onError: (error: AxiosError<APIError>) => {
      if (showToast) {
        toastAPIErrorMessage(error, "Error duplicating matter template");
      }
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [queryKeys.matterSettings] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.files] }),
      ]);
    },
    onSuccess: (response) => {
      syncMatterTemplateCaches(queryClient, response.data);
      if (showToast) {
        toast.success("Template duplicated successfully");
      }
    },
  });
};

export const useGenerateMatterTemplateDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: GenerateMatterTemplateDocumentInput) =>
      generateMatterTemplateDocument(data),

    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error generating matter template document");
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: variables.massTortId
          ? [queryKeys.massTorts]
          : [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Matter template document generated successfully");
    },
  });
};
